import { useLastBreadcrumbTitle, useUuidEntityTitle } from "@/common/helpers/breadcrumb";
import { isUuid } from "@/common/helpers/utils";
import { resolveParentContext } from "@/common/hooks/resolve-breadcrumb-title/_helpers/resolve-parent-context";

/**
 * Resolves a route to a context-rich title for recent-visit tracking.
 * Composes the nearest named ancestor with the resolved leaf (including async
 * UUID entity names), e.g. "Vehicles · Create" or "Vehicles · WVW…".
 * Returns null while a UUID leaf is still resolving so callers can defer recording.
 *
 * Uses the stable-hook-count `useUuidEntityTitle` (never the conditional-hook
 * `useBreadcrumbItemName`) so it is safe to call from a component that does not
 * remount on navigation.
 */
export function useResolvedBreadcrumbTitle(location: string): string | null {
  const pathParts = location.split("/").filter((part) => part !== "");
  const lastPart = pathParts[pathParts.length - 1] ?? "";
  const previousValues = pathParts.slice(0, -1);
  const parentSegment = previousValues[previousValues.length - 1] ?? "";
  const isUuidLeaf = isUuid(lastPart);
  const staticLeafTitle = useLastBreadcrumbTitle(location);
  const uuidEntityTitle = useUuidEntityTitle(parentSegment, isUuidLeaf ? lastPart : "");

  if (pathParts.length === 0) return null;

  const leafTitle = isUuidLeaf ? uuidEntityTitle : staticLeafTitle;
  if (!leafTitle) return null;

  const parentTitle = resolveParentContext(previousValues);
  return parentTitle && parentTitle !== leafTitle ? `${parentTitle} · ${leafTitle}` : leafTitle;
}
